/**
 * 6-Week Challenges Page
 * 
 * Time-bound community challenges with tracking, accountability, and rewards.
 * Locked until 30 days after user registration.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { parseLocalDate } from '@/utils/dateUtils';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  ArrowLeft, Loader2, Trophy, Calendar, Users, Flame, CheckCircle,
  Medal, Target, Clock, Zap, Crown, ChevronRight, Play, Star, Lock, Mic
} from 'lucide-react';
import VoiceInput from '@/components/voice/VoiceInput';

const API_URL = window.location.origin;

const CATEGORY_COLORS = {
  wellness: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' },
  career: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
  spiritual: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' },
  financial: { bg: 'bg-yellow-100', text: 'text-yellow-600', border: 'border-yellow-200' },
  relationships: { bg: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-200' },
  mindset: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' }
};

const ChallengesPage = () => {
  const { canAccessChallenges, daysUntilChallengesUnlock } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [challenges, setChallenges] = useState({ active: [], completed: [], available: [] });
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [challengeProgress, setChallengeProgress] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  
  // Dialog states
  const [showChallengeDialog, setShowChallengeDialog] = useState(false);
  const [showCheckinDialog, setShowCheckinDialog] = useState(false);
  const [showWeeklyDialog, setShowWeeklyDialog] = useState(false);
  
  // Form states
  const [checkinNotes, setCheckinNotes] = useState('');
  const [checkinCompleted, setCheckinCompleted] = useState(true);
  const [weeklyReflection, setWeeklyReflection] = useState('');
  const [weeklyGoals, setWeeklyGoals] = useState([]);
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [challengesRes, activeRes] = await Promise.all([
        axios.get(`${API_URL}/api/challenges/mine`, { headers }),
        axios.get(`${API_URL}/api/challenges/active`, { headers })
      ]);
      
      setChallenges(challengesRes.data);
      setActiveChallenges(activeRes.data.challenges || []);
    } catch (error) {
      console.error('Failed to fetch challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewChallenge = async (challenge, isUserChallenge = false) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Get challenge details if we only have participant data
      const challengeId = isUserChallenge ? challenge.challenge_id : challenge.id;
      
      const [detailRes, leaderboardRes] = await Promise.all([
        axios.get(`${API_URL}/api/challenges/${challengeId}`, { headers }),
        axios.get(`${API_URL}/api/challenges/${challengeId}/leaderboard`, { headers })
      ]);
      
      setSelectedChallenge(detailRes.data.challenge);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
      
      // Get user's progress if joined
      if (isUserChallenge || challenges.active.some(c => c.challenge_id === challengeId)) {
        const progressRes = await axios.get(`${API_URL}/api/challenges/${challengeId}/my-progress`, { headers });
        setChallengeProgress(progressRes.data);
      } else {
        setChallengeProgress(null);
      }
      
      setShowChallengeDialog(true);
    } catch (error) {
      toast.error('Failed to load challenge details');
    }
  };

  const joinChallenge = async (challengeId) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/challenges/${challengeId}/join`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      fetchData();
      setShowChallengeDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join challenge');
    } finally {
      setSaving(false);
    }
  };

  const submitDailyCheckin = async () => {
    if (!selectedChallenge || !challengeProgress) return;
    
    // Calculate current day
    const startDate = parseLocalDate(selectedChallenge.start_date);
    const today = new Date();
    const dayNumber = Math.floor((today - startDate) / (1000 * 60 * 60 * 24)) + 1;
    
    if (dayNumber < 1 || dayNumber > 42) {
      toast.error('Challenge not active for check-in');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/challenges/${selectedChallenge.id}/daily-checkin`,
        {
          day_number: dayNumber,
          completed: checkinCompleted,
          notes: checkinNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowCheckinDialog(false);
      setCheckinNotes('');
      viewChallenge({ id: selectedChallenge.id });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check in');
    } finally {
      setSaving(false);
    }
  };

  const submitWeeklyCheckin = async (weekNumber) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/challenges/${selectedChallenge.id}/weekly-checkin`,
        {
          week_number: weekNumber,
          goals_met: weeklyGoals,
          reflection: weeklyReflection
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowWeeklyDialog(false);
      setWeeklyReflection('');
      setWeeklyGoals([]);
      viewChallenge({ id: selectedChallenge.id });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit reflection');
    } finally {
      setSaving(false);
    }
  };

  const getCurrentDay = () => {
    if (!selectedChallenge) return 0;
    const startDate = parseLocalDate(selectedChallenge.start_date);
    const today = new Date();
    return Math.floor((today - startDate) / (1000 * 60 * 60 * 24)) + 1;
  };

  const getCurrentWeek = () => Math.ceil(getCurrentDay() / 7);

  // Check if challenges are locked (less than 30 days since registration)
  const challengesLocked = !canAccessChallenges();
  const daysRemaining = daysUntilChallengesUnlock();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-terracotta" />
        </div>
      </DashboardLayout>
    );
  }

  // Show locked state if user hasn't completed 30 days
  if (challengesLocked) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto" data-testid="challenges-page-locked">
          {/* Header */}
          <div className="mb-8">
            <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-600 hover:text-forest mb-4">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Dashboard
            </Link>
            
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center">
                <Lock className="h-6 w-6 text-gray-400" />
              </div>
              <div>
                <h1 className="text-2xl font-heading text-forest">6-Week Challenges</h1>
                <p className="text-gray-600">Transform together with time-bound community challenges</p>
              </div>
            </div>
          </div>

          {/* Locked Message Card */}
          <Card className="card-base border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
            <CardContent className="p-8 text-center">
              <div className="h-20 w-20 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-6">
                <Lock className="h-10 w-10 text-orange-400" />
              </div>
              
              <h2 className="text-xl font-heading text-forest mb-3">
                Almost There, Queen!
              </h2>
              
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                6-Week Challenges unlock after your first 30 days. This gives you time to settle into 
                the community and build a strong foundation with your daily practices.
              </p>
              
              {/* Countdown */}
              <div className="bg-white rounded-xl p-6 mb-6 inline-block">
                <div className="text-4xl font-heading text-orange-500 mb-1">
                  {daysRemaining}
                </div>
                <div className="text-sm text-gray-500">
                  days until unlock
                </div>
              </div>
              
              {/* Progress towards unlock */}
              <div className="max-w-xs mx-auto mb-6">
                <div className="flex items-center justify-between mb-2 text-xs text-gray-500">
                  <span>Your journey progress</span>
                  <span>{30 - daysRemaining} / 30 days</span>
                </div>
                <Progress value={((30 - daysRemaining) / 30) * 100} className="h-2" />
              </div>
              
              {/* What to do meanwhile */}
              <div className="text-left bg-white rounded-xl p-4">
                <h3 className="font-medium text-forest mb-3 text-sm">While you wait, try these:</h3>
                <div className="space-y-2">
                  <Link 
                    to="/activation-pathway" 
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-orange-50 transition-colors"
                  >
                    <Target className="h-4 w-4 text-orange-500" />
                    <span className="text-sm text-gray-600">Complete your 30-Day Activation</span>
                    <ChevronRight className="h-4 w-4 text-gray-400 ml-auto" />
                  </Link>
                  <Link 
                    to="/legacy-vision" 
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-orange-50 transition-colors"
                  >
                    <Target className="h-4 w-4 text-blue-500" />
                    <span className="text-sm text-gray-600">Complete your YUMMY Declaration</span>
                    <ChevronRight className="h-4 w-4 text-gray-400 ml-auto" />
                  </Link>
                  <Link 
                    to="/truth-reset" 
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-orange-50 transition-colors"
                  >
                    <Target className="h-4 w-4 text-purple-500" />
                    <span className="text-sm text-gray-600">Start a Truth Reset exercise</span>
                    <ChevronRight className="h-4 w-4 text-gray-400 ml-auto" />
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const ChallengeCard = ({ challenge, isUserChallenge = false, showJoin = false }) => {
    const category = challenge.category || challenge.challenge?.category;
    const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS.mindset;
    const title = challenge.title || challenge.challenge?.title;
    const participantCount = challenge.participant_count || 0;
    const status = challenge.status;
    
    return (
      <Card 
        className={`card-base cursor-pointer hover:shadow-md transition-shadow ${colors.border}`}
        onClick={() => viewChallenge(challenge, isUserChallenge)}
        data-testid={`challenge-card-${challenge.id || challenge.challenge_id}`}
      >
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className={`h-12 w-12 rounded-xl ${colors.bg} flex items-center justify-center shrink-0`}>
              <Target className={`h-6 w-6 ${colors.text}`} />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-medium text-forest truncate">{title}</h3>
                {status === 'active' && (
                  <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                )}
                {status === 'upcoming' && (
                  <Badge className="bg-blue-100 text-blue-700 text-xs">Upcoming</Badge>
                )}
              </div>
              
              {isUserChallenge && challengeProgress ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Flame className="h-4 w-4 text-orange-500" />
                    <span>{challenge.current_streak || 0} day streak</span>
                    <span className="text-gray-300">|</span>
                    <Trophy className="h-4 w-4 text-amber-500" />
                    <span>{challenge.total_points || 0} pts</span>
                  </div>
                  <Progress 
                    value={((challenge.daily_checkins ? Object.keys(challenge.daily_checkins).length : 0) / 42) * 100} 
                    className="h-1.5"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {participantCount} joined
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    6 weeks
                  </span>
                </div>
              )}
            </div>
            
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="challenges-page">
        {/* Header */}
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-600 hover:text-forest mb-4">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Link>
          
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center">
              <Flame className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-heading text-forest">6-Week Challenges</h1>
              <p className="text-gray-600">Transform together with time-bound community challenges</p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="active" className="relative">
              My Challenges
              {challenges.active?.length > 0 && (
                <Badge className="ml-2 bg-orange-500 text-white text-xs">
                  {challenges.active.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="available">Available</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
          </TabsList>

          {/* My Active Challenges */}
          <TabsContent value="active">
            {challenges.active?.length === 0 ? (
              <Card className="card-base">
                <CardContent className="p-8 text-center">
                  <Target className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-heading text-forest mb-2">No Active Challenges</h3>
                  <p className="text-gray-600 mb-4">
                    Join a challenge to start your transformation journey!
                  </p>
                  <Button 
                    onClick={() => setActiveTab('available')}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Browse Challenges
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {challenges.active.map(challenge => (
                  <ChallengeCard 
                    key={challenge.id} 
                    challenge={challenge} 
                    isUserChallenge={true}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Available Challenges */}
          <TabsContent value="available">
            {activeChallenges.length === 0 ? (
              <Card className="card-base">
                <CardContent className="p-8 text-center">
                  <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-heading text-forest mb-2">No Challenges Available</h3>
                  <p className="text-gray-600">
                    New challenges are coming soon! Check back later.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {activeChallenges.map(challenge => (
                  <ChallengeCard 
                    key={challenge.id} 
                    challenge={challenge}
                    showJoin={true}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Completed Challenges */}
          <TabsContent value="completed">
            {challenges.completed?.length === 0 ? (
              <Card className="card-base">
                <CardContent className="p-8 text-center">
                  <Trophy className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-heading text-forest mb-2">No Completed Challenges</h3>
                  <p className="text-gray-600">
                    Your completed challenges will appear here.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {challenges.completed.map(challenge => (
                  <Card key={challenge.id} className="card-base border-green-200">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-green-100 flex items-center justify-center">
                          <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-forest">{challenge.challenge?.title || 'Challenge'}</h3>
                          <div className="flex items-center gap-3 text-sm text-gray-500">
                            <span className="flex items-center gap-1 text-green-600">
                              <Trophy className="h-4 w-4" />
                              {challenge.total_points} diamonds earned
                            </span>
                            <span className="flex items-center gap-1">
                              <Flame className="h-4 w-4 text-orange-500" />
                              {challenge.longest_streak} day best streak
                            </span>
                          </div>
                        </div>
                        <Medal className="h-8 w-8 text-amber-500" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Challenge Detail Dialog */}
        <Dialog open={showChallengeDialog} onOpenChange={setShowChallengeDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedChallenge && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-3">
                    <div className={`h-12 w-12 rounded-xl ${CATEGORY_COLORS[selectedChallenge.category]?.bg || 'bg-gray-100'} flex items-center justify-center`}>
                      <Target className={`h-6 w-6 ${CATEGORY_COLORS[selectedChallenge.category]?.text || 'text-gray-600'}`} />
                    </div>
                    <div>
                      <DialogTitle className="font-heading text-xl text-forest">
                        {selectedChallenge.title}
                      </DialogTitle>
                      <p className="text-sm text-gray-500 flex items-center gap-2">
                        <Badge variant="outline">{selectedChallenge.category_info?.label}</Badge>
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {selectedChallenge.participant_count} participants
                        </span>
                      </p>
                    </div>
                  </div>
                </DialogHeader>

                <div className="py-4 space-y-6">
                  {/* Description */}
                  <p className="text-gray-600">{selectedChallenge.description}</p>

                  {/* User Progress (if joined) */}
                  {challengeProgress && (
                    <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-forest">Your Progress</h4>
                          <Badge className="bg-orange-100 text-orange-700">
                            Day {getCurrentDay()} of 42
                          </Badge>
                        </div>
                        
                        <Progress value={challengeProgress.progress_percent} className="h-2 mb-3" />
                        
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-2xl font-heading text-orange-600">
                              {challengeProgress.participant?.current_streak || 0}
                            </p>
                            <p className="text-xs text-gray-500">Current Streak</p>
                          </div>
                          <div>
                            <p className="text-2xl font-heading text-amber-600">
                              {challengeProgress.participant?.total_points || 0}
                            </p>
                            <p className="text-xs text-gray-500">Diamonds</p>
                          </div>
                          <div>
                            <p className="text-2xl font-heading text-green-600">
                              {challengeProgress.completed_days || 0}
                            </p>
                            <p className="text-xs text-gray-500">Days Done</p>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 mt-4">
                          <Button
                            onClick={() => setShowCheckinDialog(true)}
                            className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                            data-testid="daily-checkin-btn"
                          >
                            <Zap className="h-4 w-4 mr-2" />
                            Daily Check-In
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => setShowWeeklyDialog(true)}
                            className="flex-1"
                            data-testid="weekly-reflection-btn"
                          >
                            Week {getCurrentWeek()} Reflection
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Leaderboard */}
                  {leaderboard.length > 0 && (
                    <div>
                      <h4 className="font-medium text-forest mb-3 flex items-center gap-2">
                        <Trophy className="h-4 w-4 text-amber-500" />
                        Leaderboard
                      </h4>
                      <div className="space-y-2">
                        {leaderboard.slice(0, 5).map((p, i) => (
                          <div 
                            key={p.user_id}
                            className={`flex items-center gap-3 p-2 rounded-lg ${i === 0 ? 'bg-amber-50' : 'bg-gray-50'}`}
                          >
                            <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                              i === 0 ? 'bg-amber-500 text-white' : 
                              i === 1 ? 'bg-gray-400 text-white' : 
                              i === 2 ? 'bg-amber-700 text-white' : 'bg-gray-200'
                            }`}>
                              {p.rank}
                            </span>
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={p.user_image} />
                              <AvatarFallback>{p.user_name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <span className="flex-1 text-sm font-medium truncate">{p.user_name}</span>
                            <span className="text-sm text-amber-600 font-medium">{p.total_points} pts</span>
                            {p.completed && <CheckCircle className="h-4 w-4 text-green-500" />}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Weekly Goals */}
                  {selectedChallenge.weekly_goals?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-forest mb-3">Weekly Goals</h4>
                      <div className="space-y-2">
                        {selectedChallenge.weekly_goals.map((goal, i) => (
                          <div key={i} className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg">
                            <Badge variant="outline" className="shrink-0">Week {i + 1}</Badge>
                            <p className="text-sm text-gray-600">{goal}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <DialogFooter>
                  {!challengeProgress && (
                    <Button
                      onClick={() => joinChallenge(selectedChallenge.id)}
                      disabled={saving}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="join-challenge-btn"
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          Join This Challenge
                        </>
                      )}
                    </Button>
                  )}
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Daily Check-in Dialog */}
        <Dialog open={showCheckinDialog} onOpenChange={setShowCheckinDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <Zap className="h-5 w-5 text-orange-500" />
                Day {getCurrentDay()} Check-In
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              <div className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg">
                <Checkbox 
                  id="completed"
                  checked={checkinCompleted}
                  onCheckedChange={setCheckinCompleted}
                />
                <label htmlFor="completed" className="text-sm font-medium text-forest">
                  I completed today's activity
                </label>
              </div>
              
              <div>
                <label className="text-sm font-medium text-forest block mb-2">
                  Notes (optional)
                </label>
                <Textarea
                  placeholder="How did it go? Any wins or challenges?"
                  value={checkinNotes}
                  onChange={(e) => setCheckinNotes(e.target.value)}
                  rows={3}
                  spellCheck={true}
                  data-testid="challenge-checkin-notes"
                />
                <div className="mt-2">
                  <VoiceInput 
                    onTranscript={(text) => setCheckinNotes(prev => prev ? `${prev}\n\n${text}` : text)}
                  />
                </div>
              </div>
              
              <div className="p-3 bg-amber-50 rounded-lg text-sm">
                <p className="text-amber-700">
                  <Star className="h-4 w-4 inline mr-1" />
                  Earn <strong>5 diamonds</strong> for checking in!
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCheckinDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={submitDailyCheckin}
                disabled={saving}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check In'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Weekly Reflection Dialog */}
        <Dialog open={showWeeklyDialog} onOpenChange={setShowWeeklyDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-500" />
                Week {getCurrentWeek()} Reflection
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              {selectedChallenge?.weekly_goals && (
                <div>
                  <label className="text-sm font-medium text-forest block mb-2">
                    This week's goal:
                  </label>
                  <p className="text-sm text-gray-600 p-2 bg-gray-50 rounded-lg">
                    {selectedChallenge.weekly_goals[getCurrentWeek() - 1]}
                  </p>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium text-forest block mb-2">
                  Your Reflection
                </label>
                <Textarea
                  placeholder="What did you learn this week? What are you proud of?"
                  value={weeklyReflection}
                  onChange={(e) => setWeeklyReflection(e.target.value)}
                  rows={4}
                />
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <p className="text-blue-700">
                  <Trophy className="h-4 w-4 inline mr-1" />
                  Earn <strong>15 diamonds</strong> for your weekly reflection!
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowWeeklyDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={() => submitWeeklyCheckin(getCurrentWeek())}
                disabled={saving || !weeklyReflection.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit Reflection'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ChallengesPage;
